import {
  DocumentVerificationType,
  IncomeStatus,
  ReverifyRequest,
  ReverifyRequestStatus,
  ReverifyRequestType,
  Verification as SharedVerification,
  VerificationStatus,
  VerifyMethodType
} from "shared/types";
import { VerifiedItem } from "../../types/verified";
import {
  EmptyReverifyItem,
  ProcessingReverifyContent,
  ReverifyAttempt,
  ReverifyDetails,
  ReverifyFormattedDetails,
  ReverifyTabType
} from "./types";
import type { ReactNode } from "react";
import { IncomeReverifyTab } from "./components/IncomeReverifyTab";
import {
  CombinedIncome,
  VerificationSpecificAdditionaData
} from "../../types/income";
import { getVerificationTitleName } from "../../utils";
import { DateTime } from "luxon";
import {
  Loading03Icon,
  MoneyNotFound04Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { BankStatementItem } from "../../types/verified/bank-statment";
import { Verification } from "views/applications-id/components/ApplicantsTable/types";
import { VerificationReport } from "types/landlord/reports";
import { CombinedIncomeReport } from "../../types";
import { REVERIFY_FAILURE_REASONS } from "./constants";

const ORIGINAL_ITEM_GROUP_KEY = "original";

// Income source types whose items can arrive in bulk (multiple documents
// uploaded for one verification attempt) and must be grouped together
// instead of each being treated as its own reverify attempt/tab.
const GROUPABLE_INCOME_SOURCE_TYPES: VerifyMethodType[] = [
  VerifyMethodType.BankStatement,
  VerifyMethodType.W2
];

const getReverifyAttemptDateMs = (attempt: ReverifyAttempt): number => {
  if (isReverifyTabAPlaceholder(attempt)) {
    const request = attempt.reverifyRequest;
    if (!request) return 0;
    const iso = request.created_at;
    return iso ? new Date(iso).getTime() : 0;
  }
  if (Array.isArray(attempt)) {
    const iso = attempt[0]?.created_at;
    return iso ? new Date(iso).getTime() : 0;
  }
  return attempt.created_at ? new Date(attempt.created_at).getTime() : 0;
};

const isActiveReverifyAttempt = (attempt: ReverifyAttempt): boolean =>
  isReverifyTabAPlaceholder(attempt) || !isReverifyTabArchived(attempt);

export const getReverifyRequests = (
  verification?:
    | VerificationReport<CombinedIncomeReport>
    | Verification
    | SharedVerification
): {
  allReverifyRequests: ReverifyRequest[];
  pendingReverifyRequests: ReverifyRequest[];
  completedReverifyRequests: ReverifyRequest[];
  failedReverifyRequests: ReverifyRequest[];
} => {
  const combinedIncomeStep = verification?.steps?.find(
    (step) => step.name === "combined_income"
  );
  const stepData = combinedIncomeStep?.data as
    | { reverify_requests?: ReverifyRequest[] }
    | undefined;
  const allReverifyRequests = stepData?.reverify_requests ?? [];
  const pendingReverifyRequests = allReverifyRequests.filter(
    (r) => r.status === ReverifyRequestStatus.Pending
  );
  const completedReverifyRequests = allReverifyRequests.filter((r) =>
    [
      ReverifyRequestStatus.Completed,
      ReverifyRequestStatus.Processing
    ].includes(r.status)
  );
  const failedReverifyRequests = allReverifyRequests.filter(
    (r) => r.status === ReverifyRequestStatus.Failed && r.failure_reason
  );

  return {
    allReverifyRequests,
    pendingReverifyRequests,
    completedReverifyRequests,
    failedReverifyRequests
  };
};

export const getIncomeReverifyRequest = (
  incomeId: number,
  pendingReverifyRequests?: ReverifyRequest[]
) => {
  const reverifyRequest = pendingReverifyRequests?.find(
    (request) =>
      request.income_id === incomeId &&
      request.request_type === "applicant_request" &&
      request.status === ReverifyRequestStatus.Pending
  );
  return reverifyRequest;
};

export const getLatestReverifyRequestForIncome = (
  incomeId: number,
  reverifyRequests?: ReverifyRequest[] | null
): ReverifyRequest | null => {
  if (!reverifyRequests?.length) return null;
  return (
    reverifyRequests
      .filter((request) => request.income_id === incomeId)
      .sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      )[0] ?? null
  );
};

export const isReverifyTabArchived = (reverifyAttempt?: ReverifyAttempt) =>
  Boolean(
    Array.isArray(reverifyAttempt)
      ? reverifyAttempt.every((r) => r.is_archived)
      : reverifyAttempt?.is_archived
  );

const isReverifyTabFailed = (reverifyAttempt?: ReverifyAttempt) =>
  Boolean(
    isReverifyTabAPlaceholder(reverifyAttempt) &&
    reverifyAttempt.reverifyRequest?.status === ReverifyRequestStatus.Failed
  );

export const isReverifyTabABankStatement = (
  reverifyAttempt?: ReverifyAttempt | null
) =>
  Array.isArray(reverifyAttempt) &&
  reverifyAttempt[0]?.income_source_type ===
    DocumentVerificationType.BankStatement;

export const isReverifyTabAPlaceholder = (
  item: unknown
): item is EmptyReverifyItem =>
  !!item &&
  typeof item === "object" &&
  !Array.isArray(item) &&
  "reverifyRequest" in item;

/**
 * Each tab's "reverification number" reflects its chronological position
 * (oldest = "Initial verification", newer attempts ascend). The active tab
 * is pinned first, but its number still follows chronology — we look up
 * each attempt's position in a separate DESC-by-date order
 */
export const getReverifyTabs = (
  reverifyAttempts: ReverifyAttempt[]
): { tabIds: string[]; displayMap: Record<string, ReactNode> } => {
  const chronologicalOrder = [...reverifyAttempts].sort(
    (a, b) => getReverifyAttemptDateMs(b) - getReverifyAttemptDateMs(a)
  );
  const chronoIdxByAttempt = new Map<ReverifyAttempt, number>();
  chronologicalOrder.forEach((attempt, idx) => {
    chronoIdxByAttempt.set(attempt, idx);
  });

  const maxChronoIdx = chronologicalOrder.length - 1;

  return reverifyAttempts.reduce<{
    tabIds: string[];
    displayMap: Record<string, ReactNode>;
  }>(
    (acc, attempt, idx) => {
      const chronoIdx = chronoIdxByAttempt.get(attempt) ?? maxChronoIdx;
      const isLast = chronoIdx === maxChronoIdx;
      const reverifyCount = maxChronoIdx - chronoIdx;
      const isArchived = isReverifyTabArchived(attempt);
      const isFailed = isReverifyTabFailed(attempt);
      const id = String(idx);

      acc.tabIds.push(id);
      acc.displayMap[id] = (
        <IncomeReverifyTab
          isLast={isLast}
          reverifyCount={reverifyCount}
          isArchived={isArchived}
          isFailed={isFailed}
        />
      );
      return acc;
    },
    { tabIds: [], displayMap: {} }
  );
};

const getIncomeReverifyType = (
  income: CombinedIncome,
  reverifyRequest?: ReverifyRequest | null,
  verificationStatus?: VerificationStatus
): ReverifyTabType => {
  if (reverifyRequest) {
    if (
      reverifyRequest.status === ReverifyRequestStatus.Failed &&
      reverifyRequest.failure_reason
    )
      return ReverifyTabType.Failed;

    if (
      reverifyRequest.request_type === ReverifyRequestType.ApplicantRequest &&
      reverifyRequest.status === ReverifyRequestStatus.Pending
    )
      return ReverifyTabType.ApplicantRequest;

    if (
      reverifyRequest.request_type === ReverifyRequestType.DocumentsReVerify ||
      (income.status === IncomeStatus.Processing &&
        (verificationStatus === VerificationStatus.Finished ||
          [
            ReverifyRequestStatus.Completed,
            ReverifyRequestStatus.Processing
          ].includes(reverifyRequest.status)))
    )
      return ReverifyTabType.DocumentsUpload;
  }

  return ReverifyTabType.InitialVerification;
};

const formatReverifyDetails = (
  income?: CombinedIncome,
  reverifyType?: ReverifyTabType,
  reverifyRequest?: ReverifyRequest | null
): ReverifyFormattedDetails => {
  const incomeName = income ? getVerificationTitleName(income) : "-";
  const initialVerificationDetails = {
    initiatedBy: "-",
    formattedDate: "-",
    incomeName
  };

  if (!income || !reverifyRequest) return initialVerificationDetails;

  if (reverifyType === ReverifyTabType.ApplicantRequest)
    return {
      initiatedBy: reverifyRequest.created_by?.name || "-",
      formattedDate: DateTime.fromISO(reverifyRequest.created_at).toFormat(
        "DDD 'at' h:mm a"
      ),
      incomeName
    };

  if (reverifyType === ReverifyTabType.DocumentsUpload)
    return {
      initiatedBy: reverifyRequest.created_by?.name || "-",
      formattedDate: DateTime.fromISO(income.updated_at).toFormat(
        "DDD 'at' h:mm a"
      ),
      incomeName
    };

  if (reverifyType === ReverifyTabType.Failed)
    return {
      initiatedBy: reverifyRequest.created_by?.name || "-",
      formattedDate: DateTime.fromISO(reverifyRequest.updated_at).toFormat(
        "DDD 'at' h:mm a"
      ),
      incomeName
    };

  return initialVerificationDetails;
};

const getProcessingReverifyContent = (
  type: ReverifyTabType,
  formattedDetails?: ReverifyFormattedDetails | null,
  reverifyRequest?: ReverifyRequest | null
): ProcessingReverifyContent => {
  const { initiatedBy, incomeName } = formattedDetails || {};
  const reverifyDescription = `Re-verification was initiated by ${
    initiatedBy
  } for the income source "${incomeName}"`;

  switch (type) {
    case ReverifyTabType.Failed: {
      const reason = reverifyRequest?.failure_reason
        ? REVERIFY_FAILURE_REASONS[reverifyRequest.failure_reason]
        : undefined;
      return {
        title: reason?.title || "Re-verification failed",
        subtitle: reason?.subtitle,
        description: reverifyDescription,
        showDate: true,
        showCancelButton: true,
        icon: MoneyNotFound04Icon,
        iconClassName: "text-primary-500",
        iconWrapperClassName: "bg-primary-20"
      };
    }
    case ReverifyTabType.ApplicantRequest:
      return {
        title: "Re-verification request was sent to the applicant",
        description: reverifyDescription,
        showDate: true,
        showCancelButton: true,
        icon: Loading03Icon,
        iconClassName: "text-warning-600",
        iconWrapperClassName: "bg-warning-50"
      };
    case ReverifyTabType.DocumentsUpload:
      return {
        title: "Re-verifying income source",
        description: reverifyDescription,
        showDate: true,
        showCancelButton: false,
        icon: Loading03Icon,
        iconClassName: "text-warning-600",
        iconWrapperClassName: "bg-warning-50"
      };
    case ReverifyTabType.InitialVerification:
    default:
      return {
        title: "Verifying income source with documents...",
        description: "Verification may take up to an hour",
        showDate: false,
        showCancelButton: false,
        icon: Loading03Icon,
        iconClassName: "text-warning-600",
        iconWrapperClassName: "bg-warning-50"
      };
  }
};

export const getReverifyDetails = (
  reverifyingIncome: CombinedIncome,
  reverifyRequest?: ReverifyRequest | null,
  verificationStatus?: VerificationStatus
): ReverifyDetails => {
  const reverifyType = getIncomeReverifyType(
    reverifyingIncome,
    reverifyRequest,
    verificationStatus
  );
  const formattedReverifyDetails = formatReverifyDetails(
    reverifyingIncome,
    reverifyType,
    reverifyRequest
  );
  const reverifyContent = getProcessingReverifyContent(
    reverifyType,
    formattedReverifyDetails,
    reverifyRequest
  );

  return {
    reverifyType,
    ...formattedReverifyDetails,
    ...reverifyContent
  };
};

const getManualApplicantBankStatementsItems = (
  income: CombinedIncome,
  isManualApplicant: boolean,
  additionalData?: VerificationSpecificAdditionaData | null
) => {
  if (
    !isManualApplicant ||
    income.verified_source_type !== VerifyMethodType.BankStatement
  )
    return undefined;

  const incomeBankStatements =
    additionalData?.bank_statement?.allBankStatements || {};

  return income.income_sources_ids?.reduce(
    (acc, income_source_id) => {
      const items = incomeBankStatements[income_source_id].items;
      if (items.length)
        acc.push(
          items.map((item) => ({
            id: item.id,
            income_source_id: income_source_id,
            income_id: income.id,
            created_at: income.created_at,
            updated_at: income.updated_at,
            is_archived: item.is_archived || false,
            reverify_log: item.reverify_log,
            income_source_type: "bank_statement" as const,
            data: item.data
          })) as BankStatementItem[]
        );

      return acc;
    },
    [] as Array<Array<BankStatementItem>>
  );
};

const getGroupedIncomeItems = (
  income: CombinedIncome,
  manualApplicantBankStatementsItems?: Array<Array<BankStatementItem>>
) => {
  if (manualApplicantBankStatementsItems)
    return manualApplicantBankStatementsItems;

  const groupedIncomeItems: (VerifiedItem | VerifiedItem[])[] = [];
  const groupsByTypeAndReverifyId = new Map<string, VerifiedItem[]>();

  [...income.items].reverse().forEach((item) => {
    if (
      !GROUPABLE_INCOME_SOURCE_TYPES.includes(
        item.income_source_type as VerifyMethodType
      )
    ) {
      groupedIncomeItems.push(item);
      return;
    }

    const reverifyId =
      item.reverify_log?.verification_attempt_id ?? ORIGINAL_ITEM_GROUP_KEY;
    const groupKey = `${item.income_source_type}:${reverifyId}`;
    const existingGroup = groupsByTypeAndReverifyId.get(groupKey);

    if (existingGroup) {
      existingGroup.push(item);
      return;
    }

    const newGroup: VerifiedItem[] = [item];
    groupsByTypeAndReverifyId.set(groupKey, newGroup);
    groupedIncomeItems.push(newGroup);
  });

  return groupedIncomeItems;
};

/**
 * reverifyAttempts - there are such entities that are considered a re-verification:
 * 1. verification > report > incomes[x] > items[y]: for most income sources, each separate income item is a separate verification attempt;
 * 2. verification > report > incomes[x] > items[m:n]: for bank_statement income_source type, there is a group
 * of items assigned to each verification attempt. For now, grouping and identification of items that belong to the same
 * verification attempt group happens on FE;
 * 3. verification > steps[x] > data > reverify_requests: pending and (when the income is still
 * processing the just-uploaded docs) the latest completed re-verify request
 */
export const getReverifyAttemptsPerIncome = ({
  income,
  additionalData,
  pendingReverifyRequest,
  completedReverifyRequest,
  failedReverifyRequest,
  isManualApplicant
}: {
  income: CombinedIncome;
  additionalData: VerificationSpecificAdditionaData | null | undefined;
  pendingReverifyRequest?: ReverifyRequest | null;
  completedReverifyRequest?: ReverifyRequest | null;
  failedReverifyRequest?: ReverifyRequest | null;
  isManualApplicant: boolean;
}): ReverifyAttempt[] => {
  const manualApplicantBankStatementsItems =
    getManualApplicantBankStatementsItems(
      income,
      isManualApplicant,
      additionalData
    );
  const verifiedAttempts: ReverifyAttempt[] = getGroupedIncomeItems(
    income,
    manualApplicantBankStatementsItems
  );

  const activeReverifyRequest =
    pendingReverifyRequest ??
    (completedReverifyRequest && income.status === IncomeStatus.Processing
      ? completedReverifyRequest
      : null);
  const activeReverifyPlaceholder: EmptyReverifyItem | null =
    activeReverifyRequest
      ? ({
          reverifyRequest: activeReverifyRequest,
          is_archived: false
        } as EmptyReverifyItem)
      : null;
  const failedReverifyPlaceholder: EmptyReverifyItem | null =
    failedReverifyRequest
      ? ({
          reverifyRequest: failedReverifyRequest,
          is_archived: false
        } as EmptyReverifyItem)
      : null;

  if (activeReverifyPlaceholder || failedReverifyPlaceholder) {
    const overridenVerifiedAttempts = activeReverifyPlaceholder
      ? verifiedAttempts.map((attempt) =>
          Array.isArray(attempt)
            ? attempt.map((item) => ({ ...item, is_archived: true }))
            : { ...attempt, is_archived: true }
        )
      : verifiedAttempts;

    const rest = overridenVerifiedAttempts.sort(
      (a, b) => getReverifyAttemptDateMs(b) - getReverifyAttemptDateMs(a)
    );
    return [
      ...(failedReverifyPlaceholder ? [failedReverifyPlaceholder] : []),
      ...(activeReverifyPlaceholder ? [activeReverifyPlaceholder] : []),
      ...rest
    ];
  }

  const allAttempts = [...verifiedAttempts].sort(
    (a, b) => getReverifyAttemptDateMs(b) - getReverifyAttemptDateMs(a)
  );
  const activeIdx = allAttempts.findIndex(isActiveReverifyAttempt);
  if (activeIdx <= 0) return allAttempts;

  const [activeAttempt] = allAttempts.splice(activeIdx, 1);
  return [activeAttempt, ...allAttempts];
};
